import { useCallback, useEffect, useRef, useState } from "react";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import { useDispatch, useSelector } from "react-redux";
import { TransactionPerDayThunk } from "../../../../../redux/features/Settings/Statistics";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import dayjs from "dayjs";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * TransactionPerDay - A component that displays transaction per day trends over time
 * using a line graph. Supports filtering on the basis of 7 days or 28 days. or beneficiary
 * or internal.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.flow - Display flow: 'homeDashboard' or other
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @returns {JSX.Element} Rendered debit/credit amount graph component
 */

const TransactionPerDay = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst - Credit amount line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number[]} total_accounts - total number of accounts
   */
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [50, 50, 80, 70, 30, 20, 100],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    total_accounts: GlobalConfiguration?.lengthCheckSeven,
  };
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  const [selectedAccountTypeOption, setSelectedAccountTypeOption] = useState(
    globalText?.BENEFICIARY_OPTION,
  );

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);
  const toastShownRef = useRef(false);

  const dispatch = useDispatch();

  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes(
    globalText?.VIEW_STATISTICS,
  ); // Global service for statistics.

  // Select loading and error states from Redux Thunk
  const loginData = useSelector((state) => state.refreshToken.data);
  const { TransactionPerDayLoading, TransactionPerDayError } = useSelector(
    (state) => state.settingStatistics,
  );

  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  const transformReportData = (reports = []) => {
    return reports.reduce(
      (acc, item, index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(item.count);
        acc.xAxisLabels.push(item.date);

        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        xAxisLabels: [],
      },
    );
  };

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        const { defaultGraphDataItemsForXaxis, graphDataFirst, xAxisLabels } =
          transformReportData(reportData.generate_reports || []);

        // Update graph data with transformed arrays and summary totals
        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          xAxisData: xAxisLabels,
          totalTransactions: reportData.total_transactions,
        });

        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: globalText?.FETCHED_SUCCESSFULLY,
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;

        setIsError(true);
      }
    },
    [dispatch, selectedAccountTypeOption, selectedDays],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!selectedAccountTypeOption) return;

    const response = await dispatch(
      TransactionPerDayThunk({
        entity_id: entityId,
        transaction_type:
          selectedAccountTypeOption === globalText?.BENEFICIARY_OPTION
            ? globalText?.BENEFICIARY.toLowerCase()
            : selectedAccountTypeOption === globalText?.FIELD_FORCE_OPTION
              ? globalText?.FIELD_FORCE.split(" ")?.[
                  GlobalConfiguration?.lengthCheckZero
                ].toLowerCase() +
                "_" +
                globalText?.FIELD_FORCE.split(" ")?.[
                  GlobalConfiguration?.lengthCheckOne
                ].toLowerCase()
              : selectedAccountTypeOption === globalText?.REIMBURSEMENT
                ? globalText?.REIMBURSEMENT.toLowerCase()
                : globalText?.INTERNAL.toLowerCase(),
        period: selectedDays,
      }),
    );
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /* Button handler */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /*Auto-fetch on dependency change */
  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Re-runs when autoFetch, fetchPageEnabled, or selectedDays changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedAccountTypeOption]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === toCamelCase(globalText?.HOME_DASHBOARD) ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: toCamelCase(globalText?.ACCOUNT_TYPE_OPTION),
            setterState: setSelectedAccountTypeOption,
          },
        ]}
        titleText={
          globalText?.TRANSACTIONS_PER_DAY.split(" ")?.[
            GlobalConfiguration?.lengthCheckZero
          ]
        }
        flow={flow}
        autoFetch={autoFetch}
        hideTitle={
          flow === toCamelCase(globalText?.HOME_DASHBOARD) ? true : false
        }
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col Qsmd:flex-row relative overflow-hidden">
        <FecthingPage
          errorData={
            TransactionPerDayError &&
            TransactionPerDayError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={TransactionPerDayLoading || fetchPageLoading}
          isError={isError}
          handleFunction={handleFetchClick}
        />

        {/* Left column: wrapper has no intrinsic height on desktop, so the graph side decides it */}
        <div className="w-width-full flex Qsmd:flex-row flex-col">
          <div className="order-2 Qsmd:order-none Qsmd:relative Qsmd:max-w-column-width-md Qsmd:w-width-sm w-full flex-col flex-shrink-0 bg-primary-nav Qsmd:rounded-b-none Qsmd:rounded-bl-border-radius-md">
            {/* Real content: fills the wrapper on desktop, normal flow on mobile */}
            <div className="w-width-full h-auto flex flex-col items-center Qsmd:absolute Qsmd:inset-0 Qsmd:h-full flex-1 min-h-0 ">
              <div className="w-width-full flex flex-col flex-1 min-h-0">
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-sm font-semibold pb-padding-md px-padding-md pt-padding-sm">
                  Transaction per day
                </div>
                <div className="flex flex-col gap-gap-sm w-width-full overflow-y-auto Qsmd:flex-1 min-h-[var(--statistics-left-container-height)] max-h-[var(--statistics-left-container-height)] Qsmd:overflow-y-auto px-padding-md overflow-x-hidden">
                  {graphData?.dataFirst?.map((val, index) => (
                    <div
                      className="flex justify-between"
                      key={index}
                    >
                      <div className="text-text-secondary font-normal Qsmd:text-font-xxs text-font-xs">
                        {dayjs(graphData.xAxisData[index]).format("DD MMM")}
                      </div>
                      <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                        {fetchPageEnabled ? "-" : graphData.dataFirst[index]}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Border */}
                <div className=" border-b border-solid border-primary-bg mx-margin-md mt-margin-md"></div>
              </div>

              {/* Day-wise Total Row */}
              <div className="w-width-full h-auto flex justify-between mt-margin-sm pb-padding-sm px-padding-md Qsmd:my-margin-base Qsmd:pt-padding-md Qsmd:pb-padding-lg flex-shrink-0">
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs ">
                  Total
                </div>
                <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                  {fetchPageEnabled ? "-" : graphData.totalTransactions}
                </div>
              </div>
            </div>
          </div>

          {/* Line Graph Container with horizontal scrolling for many data points */}
          {/* Mobile: "contents" makes the graph and legend direct children of the outer flex so they can be ordered. Desktop: normal block. */}
          <div className="w-full overflow-x-hidden contents Qsmd:block">
            <div className="order-1 Qsmd:order-none w-width-full pb-padding-sm relative overflow-x-auto ">
              {/* Dynamic minimum width based on data points count for proper spacing */}
              <div
                style={{
                  minWidth: `${
                    Math.max(
                      graphData.xAxisData?.length ||
                        GlobalConfiguration?.lengthCheckZero,
                    ) *
                    SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint
                  }px`,
                }}
              >
                <CommonLineGraph
                  graphDataFirst={graphData.dataFirst}
                  xAxisLabels={graphData.xAxisData}
                  graphDataFirstColor="var(--settled)"
                  defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
                  amountFormatRequiredForLegend={false}
                />
              </div>
            </div>
            {/* Footer with Credit/Debit totals */}

            <div className="order-3 Qsmd:order-none w-width-full bg-primary-nav rounded-b-border-radius-md Qsmd:rounded-b-none Qsmd:rounded-br-border-radius-md">
              <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm  mx-margin-md Qsmd:border-t-0 border-t-border border-primary-bg">
                {/* Credit Amount Card */}

                <div className="flex items-center Qsmd:flex-1 gap-gap-xs ">
                  <div className="text-text-secondary text-font-xxs">
                    <div className="flex items-center">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="flex-shrink-0 pointer-events-none"
                      >
                        {/* Outer stroke ring (11x11 -> radius 5.5px) */}
                        <circle
                          cx={GlobalConfiguration?.lengthCheckSeven}
                          cy={GlobalConfiguration?.lengthCheckSeven}
                          r={
                            SettingsStatisticsConfiguration?.outerStrokeRingRadius
                          }
                          fill="none"
                          stroke="var(--settled)"
                          strokeWidth={
                            SettingsStatisticsConfiguration?.outerStrokeWidth
                          }
                          style={{ pointerEvents: "none" }}
                        />
                        {/* Inner solid circle (7x7 -> radius 3.5px) */}
                        <circle
                          cx={GlobalConfiguration?.lengthCheckSeven}
                          cy={GlobalConfiguration?.lengthCheckSeven}
                          r={
                            SettingsStatisticsConfiguration?.innerSolidCircleRadius
                          }
                          fill="var(--settled)"
                          stroke="none"
                          style={{ pointerEvents: "none" }}
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-normal">
                    : Transaction per day
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionPerDay;
